import { Component } from '@theme/component';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartCountBracket - The bracket count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartCountBracket'];

  /** @type {number} */
  get currentCartCount() {
    const text = this.refs.cartCountBracket.textContent ?? '';
    const match = text.match(/\((\d+)\)/);
    return match ? parseInt(match[1], 10) : 0;
  }

  /** @param {number} value */
  set currentCartCount(value) {
    const countElement = this.refs.cartCountBracket;
    if (!countElement) return;

    if (value <= 0) {
      countElement.textContent = '';
      countElement.hidden = true;
      return;
    }

    countElement.textContent = `(${value < 100 ? value : '99+'})`;
    countElement.hidden = false;
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);
    this.ensureCartCountIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = (event) => {
    if (event.persisted) {
      this.ensureCartCountIsCorrect();
    }
  };

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    const itemCount = event.detail.data?.itemCount ?? 0;
    const comingFromProductForm = event.detail.data?.source === 'product-form-component';

    this.renderCartCount(itemCount, comingFromProductForm);
  };

  /**
   * Renders the cart count in brackets.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} comingFromProductForm - Whether the cart update is coming from the product form.
   */
  renderCartCount = (itemCount, comingFromProductForm) => {
    const newCount = comingFromProductForm ? this.currentCartCount + itemCount : itemCount;
    this.currentCartCount = newCount;

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(newCount),
        timestamp: Date.now(),
      })
    );
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartCountIsCorrect = () => {
    if (!this.refs.cartCountBracket) return;

    const sessionStorageCount = sessionStorage.getItem('cart-count');
    if (sessionStorageCount === null) return;

    const visibleCount = String(this.currentCartCount);

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      if (value === visibleCount) return;

      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.currentCartCount = count;
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
